/**
 * Fonctions utilitaires pour le système de gestion des crédits
 * Conformément aux exigences 4.3 et 4.4
 * 
 * Ce fichier contient:
 * - calculateCreditPrice: Calcul du prix selon les paliers dégressifs
 * - formatCreditAmount: Formatage pour l'affichage
 * - getCreditPackages: Packs prédéfinis avec économies
 * - validateCreditAmount: Validation des entrées utilisateur
 */

import { CreditPackage } from '@/types/credits';

/**
 * Paliers de prix dégressifs pour les crédits
 * Conformément à l'exigence 4.4
 * 
 * Structure: [seuil minimum, prix par crédit en centimes]
 * Les paliers sont ordonnés du plus élevé au plus bas pour faciliter le calcul
 */
const PRICE_TIERS = [
  { minCredits: 500, pricePerCredit: 700 },  // 7€ par crédit
  { minCredits: 200, pricePerCredit: 800 },  // 8€ par crédit
  { minCredits: 100, pricePerCredit: 900 },  // 9€ par crédit
  { minCredits: 50, pricePerCredit: 1000 },  // 10€ par crédit
  { minCredits: 20, pricePerCredit: 1300 },  // 13€ par crédit
  { minCredits: 10, pricePerCredit: 1400 },  // 14€ par crédit
  { minCredits: 1, pricePerCredit: 1500 },   // 15€ par crédit (tarif de base)
] as const;

/**
 * Prix de base par crédit en centimes (15€)
 * Utilisé comme référence pour calculer les économies
 */
const BASE_PRICE_PER_CREDIT = 1500;

/**
 * Limites de validation pour les montants de crédits
 */
const MIN_CREDIT_AMOUNT = 1;
const MAX_CREDIT_AMOUNT = 10000;

/**
 * Calcule le prix total en centimes pour un montant de crédits donné
 * Miroir de la fonction SQL calculate_credit_price
 * 
 * Conformément à l'exigence 4.4:
 * - 1-9 crédits: 15€/crédit
 * - 10-19 crédits: 14€/crédit
 * - 20-49 crédits: 13€/crédit
 * - 50-99 crédits: 10€/crédit
 * - 100-199 crédits: 9€/crédit
 * - 200-499 crédits: 8€/crédit
 * - 500+ crédits: 7€/crédit
 * 
 * @param amount - Nombre de crédits à acheter
 * @returns Prix total en centimes
 * 
 * @example
 * calculateCreditPrice(5)   // 7500 (5 × 15€)
 * calculateCreditPrice(10)  // 14000 (10 × 14€)
 * calculateCreditPrice(100) // 90000 (100 × 9€)
 */
export function calculateCreditPrice(amount: number): number {
  // Trouver le palier approprié
  for (const tier of PRICE_TIERS) {
    if (amount >= tier.minCredits) {
      return amount * tier.pricePerCredit;
    }
  }
  
  // Fallback au tarif de base (ne devrait jamais arriver si amount >= 1)
  return amount * BASE_PRICE_PER_CREDIT;
}

/**
 * Formate un montant de crédits pour l'affichage
 * 
 * @param amount - Nombre de crédits
 * @param options - Options de formatage
 * @returns Chaîne formatée
 * 
 * @example
 * formatCreditAmount(1)     // "1 crédit"
 * formatCreditAmount(5)     // "5 crédits"
 * formatCreditAmount(1000)  // "1 000 crédits"
 * formatCreditAmount(5, { short: true })  // "5"
 */
export function formatCreditAmount(
  amount: number,
  options: {
    short?: boolean;
    withUnit?: boolean;
  } = {}
): string {
  const { short = false, withUnit = true } = options;
  
  // Formater le nombre avec des espaces comme séparateurs de milliers
  const formattedNumber = new Intl.NumberFormat('fr-FR').format(amount);
  
  // Mode court: juste le nombre
  if (short) {
    return formattedNumber;
  }
  
  // Sans unité: juste le nombre formaté
  if (!withUnit) {
    return formattedNumber;
  }
  
  // Avec unité: ajouter "crédit" ou "crédits"
  const unit = amount === 1 ? 'crédit' : 'crédits';
  return `${formattedNumber} ${unit}`;
}

/**
 * Formate un prix en centimes pour l'affichage en euros
 * 
 * @param priceInCents - Prix en centimes
 * @returns Chaîne formatée avec le symbole €
 * 
 * @example
 * formatPrice(1500)   // "15 €"
 * formatPrice(14000)  // "140 €"
 * formatPrice(1550)   // "15,50 €"
 */
export function formatPrice(priceInCents: number): string {
  const priceInEuros = priceInCents / 100;
  
  // Si c'est un nombre rond (pas de centimes), ne pas afficher les décimales
  const isRoundNumber = priceInCents % 100 === 0;
  
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: isRoundNumber ? 0 : 2,
    maximumFractionDigits: isRoundNumber ? 0 : 2,
  }).format(priceInEuros);
}

/**
 * Calcule le prix par crédit pour un montant donné
 * 
 * @param amount - Nombre de crédits
 * @returns Prix par crédit en centimes
 * 
 * @example
 * getPricePerCredit(5)   // 1500 (15€)
 * getPricePerCredit(10)  // 1400 (14€)
 * getPricePerCredit(100) // 900 (9€)
 */
export function getPricePerCredit(amount: number): number {
  for (const tier of PRICE_TIERS) {
    if (amount >= tier.minCredits) {
      return tier.pricePerCredit;
    }
  }
  return BASE_PRICE_PER_CREDIT;
}

/**
 * Calcule les économies réalisées par rapport au tarif de base
 * 
 * @param amount - Nombre de crédits
 * @returns Objet contenant les économies en centimes et en pourcentage
 * 
 * @example
 * calculateSavings(10)  // { amount: 1000, percentage: 6.67 }
 * calculateSavings(100) // { amount: 60000, percentage: 40 }
 */
export function calculateSavings(amount: number): {
  amount: number;
  percentage: number;
} {
  const actualPrice = calculateCreditPrice(amount);
  const basePrice = amount * BASE_PRICE_PER_CREDIT;
  const savingsAmount = basePrice - actualPrice;
  const savingsPercentage = (savingsAmount / basePrice) * 100;
  
  return {
    amount: savingsAmount,
    percentage: Math.round(savingsPercentage * 100) / 100, // Arrondir à 2 décimales
  };
}

/**
 * Retourne les packs de crédits prédéfinis avec calculs de prix et économies
 * Conformément à l'exigence 4.3
 * 
 * @returns Tableau de packs de crédits avec toutes les informations nécessaires
 * 
 * @example
 * const packages = getCreditPackages();
 * // [
 * //   { credits: 10, price: 14000, pricePerCredit: 1400, savings: 1000, ... },
 * //   { credits: 50, price: 50000, pricePerCredit: 1000, savings: 25000, ... },
 * //   ...
 * // ]
 */
export function getCreditPackages(): CreditPackage[] {
  const packages = [
    { credits: 10, isPopular: false },
    { credits: 50, isPopular: true },   // Pack populaire
    { credits: 100, isPopular: false },
    { credits: 500, isPopular: false },
  ];
  
  return packages.map((pkg) => {
    const price = calculateCreditPrice(pkg.credits);
    const pricePerCredit = getPricePerCredit(pkg.credits);
    const savings = calculateSavings(pkg.credits);
    
    return {
      credits: pkg.credits,
      price,
      pricePerCredit,
      savings: savings.amount,
      savingsPercentage: savings.percentage,
      isPopular: pkg.isPopular,
    };
  });
}

/**
 * Valide un montant de crédits saisi par l'utilisateur
 * Conformément à l'exigence 4.3
 * 
 * @param amount - Montant à valider
 * @returns Objet avec le statut de validation et un message d'erreur éventuel
 * 
 * @example
 * validateCreditAmount(50)     // { valid: true }
 * validateCreditAmount(0)      // { valid: false, error: "Le montant doit être au moins 1 crédit" }
 * validateCreditAmount(15000)  // { valid: false, error: "Le montant ne peut pas dépasser 10 000 crédits" }
 * validateCreditAmount(5.5)    // { valid: false, error: "Le montant doit être un nombre entier" }
 */
export function validateCreditAmount(amount: number): {
  valid: boolean;
  error?: string;
} {
  // Vérifier que c'est un nombre
  if (typeof amount !== 'number' || isNaN(amount)) {
    return {
      valid: false,
      error: 'Le montant doit être un nombre valide',
    };
  }
  
  // Vérifier que c'est un entier
  if (!Number.isInteger(amount)) {
    return {
      valid: false,
      error: 'Le montant doit être un nombre entier',
    };
  }
  
  // Vérifier le minimum
  if (amount < MIN_CREDIT_AMOUNT) {
    return {
      valid: false,
      error: `Le montant doit être au moins ${MIN_CREDIT_AMOUNT} crédit`,
    };
  }
  
  // Vérifier le maximum
  if (amount > MAX_CREDIT_AMOUNT) {
    return {
      valid: false,
      error: `Le montant ne peut pas dépasser ${formatCreditAmount(MAX_CREDIT_AMOUNT)}`,
    };
  }
  
  return { valid: true };
}

/**
 * Vérifie si un montant de crédits est valide (version booléenne simple)
 * 
 * @param amount - Montant à vérifier
 * @returns true si valide, false sinon
 * 
 * @example
 * isValidCreditAmount(50)   // true
 * isValidCreditAmount(0)    // false
 * isValidCreditAmount(5.5)  // false
 */
export function isValidCreditAmount(amount: number): boolean {
  return validateCreditAmount(amount).valid;
}

/**
 * Calcule la TVA (20%) sur un prix HT en centimes
 * 
 * @param priceHT - Prix hors taxes en centimes
 * @returns Montant de la TVA en centimes
 * 
 * @example
 * calculateVAT(10000)  // 2000 (20% de 100€)
 */
export function calculateVAT(priceHT: number): number {
  return Math.round(priceHT * 0.20);
}

/**
 * Calcule le prix TTC (avec TVA 20%) à partir d'un prix HT en centimes
 * 
 * @param priceHT - Prix hors taxes en centimes
 * @returns Prix toutes taxes comprises en centimes
 * 
 * @example
 * calculateTotalTTC(10000)  // 12000 (100€ + 20€ TVA)
 */
export function calculateTotalTTC(priceHT: number): number {
  return priceHT + calculateVAT(priceHT);
}

/**
 * Obtient le nom du palier de prix pour un montant donné
 * Utile pour l'affichage dans l'interface
 * 
 * @param amount - Nombre de crédits
 * @returns Nom du palier
 * 
 * @example
 * getTierName(5)    // "Tarif standard"
 * getTierName(50)   // "Pack entreprise"
 * getTierName(500)  // "Pack premium"
 */
export function getTierName(amount: number): string {
  if (amount >= 500) return 'Pack premium';
  if (amount >= 200) return 'Pack professionnel';
  if (amount >= 100) return 'Pack avancé';
  if (amount >= 50) return 'Pack entreprise';
  if (amount >= 20) return 'Pack équipe';
  if (amount >= 10) return 'Pack démarrage';
  return 'Tarif standard';
}

/**
 * Calcule le prochain palier de prix et les économies potentielles
 * Utile pour encourager l'achat de plus de crédits
 * 
 * @param currentAmount - Montant actuel de crédits
 * @returns Informations sur le prochain palier ou null si déjà au meilleur palier
 * 
 * @example
 * getNextTier(5)
 * // {
 * //   minCredits: 10,
 * //   pricePerCredit: 1400,
 * //   additionalSavings: 500,
 * //   creditsToNextTier: 5
 * // }
 */
export function getNextTier(currentAmount: number): {
  minCredits: number;
  pricePerCredit: number;
  additionalSavings: number;
  creditsToNextTier: number;
} | null {
  // Trouver le palier actuel
  const currentTier = PRICE_TIERS.find(tier => currentAmount >= tier.minCredits);
  if (!currentTier) return null;
  
  // Trouver le prochain palier (celui juste avant dans le tableau)
  const currentIndex = PRICE_TIERS.indexOf(currentTier);
  if (currentIndex === 0) return null; // Déjà au meilleur palier
  
  const nextTier = PRICE_TIERS[currentIndex - 1];
  const creditsToNextTier = nextTier.minCredits - currentAmount;
  
  // Calculer les économies supplémentaires
  const currentPricePerCredit = currentTier.pricePerCredit;
  const nextPricePerCredit = nextTier.pricePerCredit;
  const savingsPerCredit = currentPricePerCredit - nextPricePerCredit;
  const additionalSavings = savingsPerCredit * nextTier.minCredits;
  
  return {
    minCredits: nextTier.minCredits,
    pricePerCredit: nextTier.pricePerCredit,
    additionalSavings,
    creditsToNextTier,
  };
}
